import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from '@react-spring/web';
import api from '../api/axios';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function PaymentForm({ payment, onSave, onCancel }) {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    booking_id: '',
    amount: '',
    payment_method: '',
    status: 'pending',
    transaction_id: ''
  });
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [existingPayments, setExistingPayments] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const progressSpring = useSpring({
    width: loading ? '100%' : '0%',
    config: { tension: 200, friction: 25 }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/bookings');
        const bookingsData = res.data?.data || res.data || [];
        setBookings(bookingsData);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      }
    };
    
    fetchData();
    
    if (payment) {
      setFormData({
        booking_id: payment.booking_id || '',
        amount: payment.amount || '',
        payment_method: payment.payment_method || '',
        status: payment.status || 'pending',
        transaction_id: payment.transaction_id || ''
      });
    }
  }, [payment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      if (payment) {
        await api.put(`/payments/${payment.id}`, formData);
      } else {
        await api.post('/payments', formData);
      }
      onSave();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save payment';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        onClick={(e) => e.target === e.currentTarget && onCancel()}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-2xl border w-full max-w-2xl max-h-[90vh] overflow-y-auto mt-20`}
        >
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`sticky top-0 ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'} backdrop-blur-xl border-b p-6 rounded-t-2xl`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <motion.div 
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", damping: 15, delay: 0.2 }}
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 shadow-lg ${isDark ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h2 className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{payment ? t('edit_payment') : t('add_payment')}</h2>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm`}>{t('manage_payment_info')}</p>
                </motion.div>
              </div>
              <motion.button 
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onCancel}
                className={`p-2 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
              >
                <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>
          </motion.div>

          {/* Form Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6"
          >
            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl"
                >
                  <div className="flex items-center">
                    <motion.svg 
                      initial={{ rotate: -90 }}
                      animate={{ rotate: 0 }}
                      className="w-5 h-5 mr-2" 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </motion.svg>
                    {error}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.form 
              onSubmit={handleSubmit} 
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, staggerChildren: 0.1 }}
            >
              {/* Progress Bar */}
              {loading && (
                <div className="w-full bg-gray-200 rounded-full h-1 mb-4">
                  <animated.div 
                    style={progressSpring}
                    className={`${'bg-gradient-to-r from-blue-500 to-blue-600'} h-1 rounded-full`}
                  />
                </div>
              )}

              <div>
                <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('booking')} *</label>
                <select
                  value={formData.booking_id}
                  onChange={async (e) => {
                    const bookingId = e.target.value;
                    const booking = bookings.find(b => b.id == bookingId);
                    setSelectedBooking(booking);
                    
                    if (booking) {
                      try {
                        const paymentsRes = await api.get('/payments');
                        const payments = paymentsRes.data?.data || paymentsRes.data || [];
                        const existingTotal = payments
                          .filter(p => p.booking_id == bookingId && p.status !== 'failed')
                          .reduce((sum, p) => sum + Number(p.amount), 0);
                        
                        setExistingPayments(existingTotal);
                        const remainingAmount = booking.total_amount - existingTotal;
                        
                        setFormData({
                          ...formData, 
                          booking_id: bookingId,
                          amount: remainingAmount > 0 ? remainingAmount : ''
                        });
                      } catch (error) {
                        console.error('Error fetching payments:', error);
                        setFormData({
                          ...formData, 
                          booking_id: bookingId,
                          amount: booking?.total_amount || ''
                        });
                      }
                    } else {
                      setFormData({...formData, booking_id: bookingId, amount: ''});
                      setExistingPayments(0);
                    }
                  }}
                  className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                  required
                >
                  <option value="">{t('select_booking')}</option>
                  {Array.isArray(bookings) && bookings.map(booking => (
                    <option key={booking.id} value={booking.id}>
                      Booking #{booking.id} - {booking.user?.name || `User ${booking.user_id}`} - {booking.property?.name || booking.property_id} - Rp {Number(booking.total_amount || 0).toLocaleString()}
                    </option>
                  ))}
                </select>
                {selectedBooking && (
                  <div className={`text-sm ${isDark ? 'text-gray-300 bg-gray-700/50 border-gray-600' : 'text-gray-600 bg-blue-50 border-blue-100'} mt-2 p-3 rounded-xl border`}>
                    <p><span className={`font-semibold ${isDark ? 'text-gray-200' : ''}`}>{t('user_label')}:</span> {selectedBooking.user?.name || `User ${selectedBooking.user_id}`}</p>
                    <p><span className={`font-semibold ${isDark ? 'text-gray-200' : ''}`}>{t('property_label')}:</span> {selectedBooking.property?.name || selectedBooking.property_id}</p>
                    <p><span className={`font-semibold ${isDark ? 'text-gray-200' : ''}`}>{t('total_amount_label')}:</span> Rp {Number(selectedBooking.total_amount || 0).toLocaleString()}</p>
                    <p><span className={`font-semibold ${isDark ? 'text-gray-200' : ''}`}>{t('paid_amount')}:</span> Rp {Number(existingPayments).toLocaleString()}</p>
                    <p><span className={`font-semibold ${isDark ? 'text-gray-200' : ''}`}>{t('remaining_amount')}:</span> Rp {Number(selectedBooking.total_amount - existingPayments).toLocaleString()}</p>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('payment_amount')} *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                    max={selectedBooking ? selectedBooking.total_amount - existingPayments : ''}
                    required
                  />
                  {selectedBooking && (
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                      {t('max_amount')}: Rp {Number(selectedBooking.total_amount - existingPayments).toLocaleString()}
                    </p>
                  )}
                </div>
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('payment_method_label')} *</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                    required
                  >
                    <option value="">{t('select_payment_method')}</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="GoPay">GoPay</option>
                    <option value="OVO">OVO</option>
                    <option value="DANA">DANA</option>
                    <option value="Cash">Cash</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('status')}</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                  >
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>{t('transaction_id_label')}</label>
                  <input
                    type="text"
                    value={formData.transaction_id}
                    onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                    className={`w-full p-3 border border-gray-300 rounded-xl ${isDark ? 'bg-gray-700/50 text-gray-100 border-gray-600' : 'bg-white/50'} focus:ring-2 ${'focus:ring-blue-500 focus:border-blue-500'} transition-all hover:shadow-md`}
                    placeholder={t('optional_field')}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className={`flex flex-col sm:flex-row gap-3 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              >
                <motion.button 
                  type="submit" 
                  disabled={loading || (selectedBooking && (selectedBooking.total_amount - existingPayments) <= 0)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex-1 text-white px-6 py-3 rounded-xl font-semibold focus:ring-4 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 focus:ring-gray-500' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-200'}`}
                >
                  {loading ? (
                    <>
                      <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
                      />
                      {t('saving')}
                    </>
                  ) : (
                    <>
                      <motion.svg 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5 }}
                        className="w-5 h-5 mr-2" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </motion.svg>
                      {payment ? t('update_payment') : t('create_payment')}
                    </>
                  )}
                </motion.button>
                <motion.button 
                  type="button" 
                  onClick={onCancel}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-6 py-3 border ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} rounded-xl font-semibold focus:ring-4 focus:ring-gray-200 transition-all duration-200 flex items-center justify-center`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {t('cancel')}
                </motion.button>
              </motion.div>
            </motion.form>
          </motion.div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}